import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "@/components/icons";
import { Modal } from "@/components/modal";
import { Tabs } from "@/components/tabs";
import { formatDate } from "../../_lib/format";
import { toIsoCode, isoCountryName } from "@/app/(app)/_lib/iso-countries";
import { CountryTag } from "@/components/country-tag";
import { Flag } from "@/components/flag";
import { entityTypeIcon } from "@/components/entity-type";
import { ScoreChip } from "@/components/score-chip";
import {
  DEFAULT_DECISION_STATUS,
  MatchDecisionPanel,
  STATUS_BADGE,
  STATUS_ICON,
  STATUS_LABELS,
  STATUS_ORDER,
} from "@/components/match-decision-panel";
import type {
  ListingIdentifier,
  Position,
  Match,
  MatchDecision,
  MatchDecisionStatus,
} from "../_data/types";

type Props = {
  match: Match;
  position: { current: number; total: number };
  onBack?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  embedded?: boolean;
  enableDecisions?: boolean;
  onDecisionSave?: (decision: MatchDecision) => void | Promise<void>;
  showEntityMeta?: boolean;
  showAliases?: boolean;
};

const ALL_TAB_ITEMS = [
  { id: "general", label: "General" },
  { id: "additional", label: "Additional info" },
  { id: "status", label: "Status" },
];

function additionalInfoEntries(match: Match): [string, string][] {
  return Object.entries(match.additionalInformation ?? {});
}

function hasAdditionalInfo(match: Match): boolean {
  return additionalInfoEntries(match).length > 0 || Boolean(match.remarks?.[0]);
}

export function MatchDetail({
  match,
  position,
  onBack,
  onPrev,
  onNext,
  embedded = false,
  enableDecisions = false,
  onDecisionSave,
  showEntityMeta = false,
  showAliases = false,
}: Props) {
  const isAdverseMedia = match.collection === "adverse_media";
  const showAdditionalTab = !isAdverseMedia && hasAdditionalInfo(match);
  const tabItems = ALL_TAB_ITEMS.filter((t) => {
    if (t.id === "additional") return showAdditionalTab;
    if (t.id === "status") return enableDecisions;
    return true;
  });

  const [tab, setTab] = useState<string>("general");
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState<MatchDecision | undefined>(match.decision);
  const [pendingStatus, setPendingStatus] = useState<MatchDecisionStatus>(
    match.decision?.status ?? DEFAULT_DECISION_STATUS,
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  useEffect(() => {
    setDecision(match.decision);
    setPendingStatus(match.decision?.status ?? DEFAULT_DECISION_STATUS);
    if (!showAdditionalTab) setTab("general");
  }, [match.id, match.decision, match.collection, showAdditionalTab]);

  const currentStatus = decision?.status ?? DEFAULT_DECISION_STATUS;
  function closeDecision() {
    setDecisionOpen(false);
    setPendingStatus(currentStatus);
  }

  const card = (
    <div className="card @container flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <DetailHeader
        match={match}
        position={position}
        onPrev={onPrev}
        onNext={onNext}
        onClose={onBack}
        decision={decision}
        showStatus={enableDecisions}
        showEntityMeta={showEntityMeta}
        showAliases={showAliases}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Tabs
          tabs={tabItems}
          value={tab}
          onChange={setTab}
          trailing={isAdverseMedia && <AdverseMediaActions match={match} />}
        />
        {enableDecisions && (
          <Tabs
            className="ml-auto"
            tabs={STATUS_ORDER.map((s) => ({ id: s, label: STATUS_LABELS[s] }))}
            value={pendingStatus}
            onChange={(id) => {
              setPendingStatus(id as MatchDecisionStatus);
              setSaveError(undefined);
              setDecisionOpen(true);
            }}
          />
        )}
      </div>

      <div className="@container flex min-h-0 flex-1 items-stretch gap-1 overflow-hidden rounded-lg border border-grey-200 bg-grey-100 p-1">
        {tab === "general" &&
          (match.collection === "adverse_media" ? (
            <AdverseMediaGeneralPanel match={match} />
          ) : (
            <GeneralPanel match={match} />
          ))}
        {tab === "additional" && <AdditionalInfoPanel match={match} />}
        {tab === "status" && <StatusPanel decision={decision} />}
      </div>

      {enableDecisions && (
        <Modal
          open={decisionOpen}
          onClose={closeDecision}
          title="Update status"
          description={`Set "${match.name}" to ${STATUS_LABELS[pendingStatus]} and add an optional comment.`}
          width={640}
          padding={24}
        >
          <MatchDecisionPanel
            key={`${match.id}-${String(decisionOpen)}`}
            decision={decision}
            status={pendingStatus}
            saving={saving}
            error={saveError}
            onSave={async (d) => {
              if (!onDecisionSave) {
                setDecision(d);
                setDecisionOpen(false);
                return;
              }
              setSaving(true);
              setSaveError(undefined);
              try {
                await onDecisionSave(d);
                setDecision(d);
                setDecisionOpen(false);
              } catch {
                setSaveError("Couldn't save your decision. Try again.");
              } finally {
                setSaving(false);
              }
            }}
            onCancel={closeDecision}
          />
        </Modal>
      )}
    </div>
  );

  if (embedded) return card;

  return <section className="flex min-h-0 flex-1 flex-col">{card}</section>;
}

function DetailHeader({
  match,
  position,
  onPrev,
  onNext,
  onClose,
  decision,
  showStatus,
  showEntityMeta,
  showAliases,
}: {
  match: Match;
  position: { current: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
  onClose?: () => void;
  decision?: MatchDecision;
  showStatus: boolean;
  showEntityMeta: boolean;
  showAliases: boolean;
}) {
  const pager = (className: string) => (
    <div className={`flex shrink-0 items-center gap-1 ${className}`}>
      <span className="px-1 text-small font-medium tabular-nums text-grey-900">
        {position.current}/{position.total}
      </span>
      <NavButton
        icon="keyboard_arrow_up"
        label="Previous match"
        onClick={onPrev}
        disabled={!onPrev}
      />
      <NavButton
        icon="keyboard_arrow_down"
        label="Next match"
        onClick={onNext}
        disabled={!onNext}
      />
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="ml-1 flex size-9 shrink-0 items-center justify-center rounded-sm border border-grey-200 bg-white text-grey-900 shadow-flat transition-colors hover:bg-grey-50 hover:text-black"
        >
          <Icon name="close" size={20} />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-2 @2xl:flex-row @2xl:flex-wrap @2xl:items-center">
      <div className="flex w-full items-center gap-2 @2xl:w-auto">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-grey-100 text-grey-900">
          <Icon name={entityTypeIcon(match.type)} size={20} />
        </div>
        <h3
          className="min-w-0 flex-1 truncate text-h4 text-black @2xl:flex-none"
          title={match.additionalInformation?.["Full Title"] ?? match.name}
        >
          {match.name}
        </h3>
        {showAliases && match.alternativeNames.length > 0 && (
          <span
            className="hidden min-w-0 max-w-70 truncate text-small text-grey-500 @2xl:inline-block"
            title={match.alternativeNames.map((a) => a.name).join(" · ")}
          >
            {match.alternativeNames.map((a) => a.name).join(" · ")}
          </span>
        )}
        {pager("@2xl:hidden")}
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-2 @2xl:contents">
        {showEntityMeta && flagIso(match.country) && (
          <Flag
            iso={flagIso(match.country)!}
            size={20}
            className="shrink-0"
            title={match.country}
          />
        )}
        {showEntityMeta && <ScoreChip score={match.score} size="sm" />}
        <SourceBadge source={match.source} />
        {showStatus && <StatusBadge decision={decision} />}
      </div>
      {pager("ml-auto hidden @2xl:flex")}
    </div>
  );
}

function StatusBadge({ decision }: { decision?: MatchDecision }) {
  const status = decision?.status ?? DEFAULT_DECISION_STATUS;
  return (
    <span
      className={[
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-sm px-2 text-small font-medium transition-opacity hover:opacity-80",
        STATUS_BADGE[status],
      ].join(" ")}
    >
      <Icon name={STATUS_ICON[status]} size={20} />
      {STATUS_LABELS[status]}
    </span>
  );
}

const ADV_MEDIA_SOURCE = "ADV-MEDIA";

function SourceBadge({ source }: { source: string }) {
  const isAdvMedia = source === ADV_MEDIA_SOURCE;
  return (
    <span
      className={[
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-sm px-2 text-small font-medium",
        isAdvMedia ? "text-white" : "bg-grey-200 text-grey-900",
      ].join(" ")}
      style={isAdvMedia ? { backgroundColor: "#f6b733" } : undefined}
    >
      <Icon name="info" size={20} />
      {source}
    </span>
  );
}

function NavButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex size-9 items-center justify-center rounded-sm border-t border-grey-900 bg-grey-950 text-white shadow-flat-black transition-colors hover:bg-grey-900 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon name={icon} size={20} />
    </button>
  );
}

function GeneralPanel({ match }: { match: Match }) {
  return (
    <div className="flex flex-col @3xl:flex-row @3xl:items-stretch flex-1 min-w-0 gap-1 overflow-y-auto">
      <DataCard>
        <SectionHeader icon="fact_check" title="Match details" />
        <DataRow icon="readiness_score" label="Confidence Score:">
          <ScoreChip score={match.score} size="sm" />
        </DataRow>
        {match.country && (
          <DataRow icon="public" label="Country of Residence:">
            <span className="flex flex-wrap items-center gap-1.5 font-medium text-black">
              {match.country.split(", ").map((country) => (
                <CountryTag key={country} country={country} />
              ))}
            </span>
          </DataRow>
        )}

        {match.type === "Individual" && match.dob && (
          <DataRow icon="calendar_today" label="DOB:">
            <span className="font-medium text-black">{formatDate(match.dob)}</span>
          </DataRow>
        )}
        {match.siIdentifier && (
          <DataRow icon="barcode" label="ID:">
            <span className="font-medium text-black">{match.siIdentifier}</span>
          </DataRow>
        )}
        <DataRow icon={entityTypeIcon(match.type)} label="Entity Type:">
          <span className="font-medium text-black">{match.type}</span>
        </DataRow>
        {(match.identifiers?.length ?? 0) > 0 && (
          <DataBlock icon="fingerprint" label="Listing Identifiers:">
            <BulletList items={match.identifiers!.map(formatListingIdentifier)} />
          </DataBlock>
        )}
        {(match.positions?.length ?? 0) > 0 && (
          <DataBlock icon="work" label="Position(s):">
            <BulletList items={match.positions!.map(formatPosition)} />
          </DataBlock>
        )}
      </DataCard>

      {match.alternativeNames.length > 0 && (
        <DataCard>
          <SectionHeader icon="badge" title="Aliases" />
          <BulletList items={match.alternativeNames.map((a) => a.name)} />
        </DataCard>
      )}

      <DataCard>
        <SectionHeader icon="distance" title="Locations & watchlist" />
        {match.knownLocations.length > 0 && (
          <DataBlock icon="distance" label="Known locations:">
            <BulletList items={match.knownLocations.map((l) => l.value)} />
          </DataBlock>
        )}
        <DataRow icon="language" label="Watchlist:">
          <SourceLink source={match.source} href={match.sourceListUrl} />
        </DataRow>
        {match.sourceInformationUrl && (
          <DataRow icon="description" label="Source information:">
            <ExternalLink href={match.sourceInformationUrl} label="view source" />
          </DataRow>
        )}
      </DataCard>
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-grey-200 pb-2 mb-1 text-small">
      <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
      <span className="font-medium text-black">{title}</span>
    </div>
  );
}

function AdditionalInfoPanel({ match }: { match: Match }) {
  const entries = additionalInfoEntries(match);
  const remark = match.remarks?.[0] ?? null;

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-y-auto">
      <DataCard>
        <SectionHeader icon="info" title="Additional information" />
        <div className="grid grid-cols-1 gap-x-8 @2xl:grid-cols-2">
          {entries.map(([key, value]) => (
            <div
              key={key}
              className="flex flex-col gap-0.5 border-b border-grey-100 py-1.5 last:border-b-0 text-small"
            >
              <div className="flex items-center gap-2">
                <Icon name="label" size={20} className="shrink-0 text-grey-900" />
                <span className="text-grey-900">{humanizeKey(key)}:</span>
              </div>
              <div className="min-w-0 pl-7 font-medium text-black wrap-anywhere">
                {/^https?:\/\/\S+$/.test(value) ? <ExternalLink href={value} /> : value}
              </div>
            </div>
          ))}
        </div>
        {remark && (
          <DataRow icon="label" label="Remark:">
            <span className="font-medium wrap-break-word text-black">{remark}</span>
          </DataRow>
        )}
      </DataCard>
    </div>
  );
}

function StatusPanel({ decision }: { decision?: MatchDecision }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-y-auto">
      <DataCard>
        <SectionHeader icon="history" title="Status" />
        {decision ? (
          <>
            <DataRow icon="calendar_today" label="Last edited:">
              <span className="font-medium text-black">{formatDate(decision.decidedAt)}</span>
            </DataRow>
            <DataRow icon="label" label="Comment:">
              <span className="font-medium wrap-break-word text-black">
                {decision.comment || "—"}
              </span>
            </DataRow>
          </>
        ) : (
          <p className="text-small text-grey-900">No status changes recorded yet.</p>
        )}
      </DataCard>
    </div>
  );
}

function AdverseMediaActions({ match }: { match: Match }) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const info = match.additionalInformation ?? {};
  const articleUrl = info["Article URL"];
  const articleBody = match.articleBody;

  return (
    <>
      <div className="flex shrink-0 items-center gap-2">
        <a
          href={articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!articleUrl}
          className={[
            "inline-flex items-center gap-1.5 rounded-sm border border-grey-200 bg-white px-3 py-1.5 text-small font-medium text-grey-900 shadow-flat transition-colors",
            articleUrl ? "hover:bg-grey-50 hover:text-black" : "pointer-events-none opacity-40",
          ].join(" ")}
        >
          <Icon name="insert_link" size={20} />
          Go to source
        </a>
        {articleBody && (
          <button
            type="button"
            onClick={() => setArchiveOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-[4px] border-b border-green-600 bg-green-500 px-3 py-1.5 text-small font-medium text-white shadow-flat-green transition-colors hover:bg-green-400"
          >
            <Icon name="article" size={20} />
            Read in archive
          </button>
        )}
      </div>
      {articleBody && (
        <ArchivedCopyModal
          open={archiveOpen}
          onClose={() => setArchiveOpen(false)}
          match={match}
          body={articleBody}
        />
      )}
    </>
  );
}

function ArchivedCopyModal({
  open,
  onClose,
  match,
  body,
}: {
  open: boolean;
  onClose: () => void;
  match: Match;
  body: string;
}) {
  const info = match.additionalInformation ?? {};
  const fullTitle = info["Full Title"] ?? match.name;
  const keywords = info["Keywords"] ? info["Keywords"].split(", ").filter(Boolean) : [];

  return (
    <Modal open={open} onClose={onClose} title={fullTitle} width={640} padding={24}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small text-grey-900">
        <span
          className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-tiny font-medium text-white"
          style={{ backgroundColor: "#f6b733" }}
        >
          ADV-MEDIA
          <Icon name="info" size={20} />
        </span>
        {info["Media Source"] && <span>{info["Media Source"]}</span>}
        {match.lastUpdated && <span>Published {formatDate(match.lastUpdated)}</span>}
        {info["Language"] && <span>{info["Language"].toUpperCase()}</span>}
      </div>

      <p className="whitespace-pre-line text-small leading-relaxed text-black">{body}</p>

      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {keywords.map((k) => (
            <span
              key={k}
              className="rounded-sm bg-grey-100 px-2 py-1 text-tiny font-medium text-grey-900"
            >
              {k}
            </span>
          ))}
        </div>
      )}
    </Modal>
  );
}

function AdverseMediaGeneralPanel({ match }: { match: Match }) {
  const info = match.additionalInformation ?? {};
  const individuals = info["Individuals"] ? info["Individuals"].split(", ").filter(Boolean) : [];
  const entities = info["Entities"] ? info["Entities"].split(", ").filter(Boolean) : [];

  return (
    <div className="flex flex-col @3xl:flex-row @3xl:items-stretch flex-1 min-w-0 gap-1 overflow-y-auto">
      <DataCard>
        <SectionHeader icon="article" title="Match details" />
        <DataRow icon="readiness_score" label="Confidence Score:">
          <ScoreChip score={match.score} size="sm" />
        </DataRow>
        {match.country && (
          <DataRow icon="public" label="Country:">
            <span className="flex flex-wrap items-center gap-1.5 font-medium text-black">
              {match.country.split(", ").map((country) => (
                <CountryTag key={country} country={country} />
              ))}
            </span>
          </DataRow>
        )}
        {match.lastUpdated && (
          <DataRow icon="calendar_today" label="Published:">
            <span className="font-medium text-black">{formatDate(match.lastUpdated)}</span>
          </DataRow>
        )}
        {match.siIdentifier && (
          <DataRow icon="barcode" label="ID:">
            <span className="font-medium text-black">{match.siIdentifier}</span>
          </DataRow>
        )}
        <DataRow icon={entityTypeIcon(match.type)} label="Entity Type:">
          <span className="font-medium text-black">{match.type}</span>
        </DataRow>
        {info["Language"] && (
          <DataRow icon="translate" label="Language:">
            <span className="font-medium text-black">{info["Language"]}</span>
          </DataRow>
        )}
      </DataCard>

      {(match.remarks[0] || individuals.length > 0 || entities.length > 0) && (
        <DataCard>
          <SectionHeader icon="person_search" title="Mentions" />
          {match.remarks[0] && (
            <DataBlock icon="summarize" label="Summary:">
              <p className="ml-7 text-grey-900">{match.remarks[0]}</p>
            </DataBlock>
          )}
          {individuals.length > 0 && (
            <DataBlock icon="people" label="Individuals:">
              <BulletList items={individuals} />
            </DataBlock>
          )}
          {entities.length > 0 && (
            <DataBlock icon="corporate_fare" label="Entities:">
              <BulletList items={entities} />
            </DataBlock>
          )}
        </DataCard>
      )}

      <DataCard>
        <SectionHeader icon="distance" title="Location and source" />
        {info["Media Source"] && (
          <DataRow icon="newspaper" label="Media Source:">
            {info["Source URL"] ? (
              <ExternalLink href={info["Source URL"]} label={info["Media Source"]} />
            ) : (
              <span className="font-medium text-black">{info["Media Source"]}</span>
            )}
          </DataRow>
        )}
        {info["Keywords"] && (
          <DataRow icon="sell" label="Keywords:">
            <span className="font-medium text-black">{info["Keywords"]}</span>
          </DataRow>
        )}
        {info["Image"] && (
          <DataRow icon="image" label="Image:">
            <ExternalLink href={info["Image"]} />
          </DataRow>
        )}
        <DataRow icon="language" label="Watchlist:">
          <SourceLink source={match.source} href={match.sourceListUrl} />
        </DataRow>
      </DataCard>
    </div>
  );
}

function ExternalLink({ href, label }: { href: string; label?: string }) {
  const display = label ?? href.replace(/^https?:\/\//, "");
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-green-700 hover:underline"
    >
      {display}
      <Icon name="arrow_outward" size={20} className="ml-0.5 inline-block align-text-bottom" />
    </a>
  );
}

function DataCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 min-w-0 flex-col rounded-sm border border-grey-200 bg-white p-4 shadow-flat">
      {children}
    </div>
  );
}

function DataRow({ icon, label, children }: { icon: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-b border-grey-100 py-1.5 last:border-b-0 text-small">
      <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
      <span className="shrink-0 text-grey-900">{label}</span>
      <div className="min-w-0 flex-1 wrap-anywhere">{children}</div>
    </div>
  );
}

function DataBlock({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-grey-100 py-1.5 last:border-b-0 text-small">
      <div className="flex items-center gap-2">
        <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
        <span className="font-medium text-black">{label}</span>
      </div>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="ml-7 flex list-disc flex-col gap-1 text-grey-900 marker:text-grey-500">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function SourceLink({ source, href }: { source: string; href?: string }) {
  const isAdvMedia = source === ADV_MEDIA_SOURCE;
  if (isAdvMedia) {
    return (
      <span
        className="inline-flex items-center rounded-sm px-2 py-0.5 text-small font-medium text-white"
        style={{ backgroundColor: "#f6b733" }}
      >
        {source}
      </span>
    );
  }

  if (!href) {
    return <span className="text-small font-medium text-black">{source}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 text-small font-medium text-green-700 hover:underline"
    >
      {source}
      <Icon name="arrow_outward" size={20} />
    </a>
  );
}

function flagIso(country: string): string | null {
  if (!country) return null;
  return toIsoCode(country) || null;
}

const LABEL_ACRONYMS = new Set(["url", "id", "ids", "eu", "uk", "un", "us", "dob", "ofac"]);

function humanizeKey(key: string): string {
  const words = key.split("_").filter(Boolean);
  if (words.length === 0) return key;
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (LABEL_ACRONYMS.has(lower)) return lower.toUpperCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}

function formatListingIdentifier(id: ListingIdentifier): string {
  const label = humanizeKey(id.type);
  const iso = id.country ? toIsoCode(id.country) : "";
  const detail = iso ? isoCountryName(iso) : (id.country ?? id.notes);
  return detail ? `${label}: ${id.identifier} (${detail})` : `${label}: ${id.identifier}`;
}

function formatPositionDateRange(p: Position): string | undefined {
  const start = p.startDate ? formatDate(p.startDate) : undefined;
  const end = p.endDate ? formatDate(p.endDate) : undefined;
  if (start && end) return `${start} – ${end}`;
  if (start) return `Since ${start}`;
  if (end) return `Until ${end}`;
  return undefined;
}

function formatPosition(p: Position): string {
  const iso = p.country ? toIsoCode(p.country) : "";
  const countryDetail = iso ? isoCountryName(iso) : p.country;
  const extras = [countryDetail, p.notes].filter(Boolean).join(", ");
  const base = p.organization ? `${p.title} – ${p.organization}` : p.title;
  const withExtras = extras ? `${base} (${extras})` : base;
  const dateRange = formatPositionDateRange(p);
  return dateRange ? `${withExtras} · ${dateRange}` : withExtras;
}
